/* TWRD: Quick Add on the cart drawer's recommendation cards.

   Every other card on the site gets its bag button from Liquid, in
   snippets/product-card.liquid, guarded by `twrd_cards and current_product`.
   The cart drawer cannot use that path: its cards are cloned from a
   <template> that Liquid renders with no product at all, and the vendor
   element (assets/cart-recommendations.js -> product-cards.js) fills them in
   from JSON afterwards. So the Liquid guard is false and the button is absent,
   which left the drawer as the one place on the site with no way to add a
   recommended product.

   Rather than patch a minified vendor bundle or keep a second copy of the card
   markup in sync, this watches the recommendation container and adds the same
   button the Liquid card would have. The button needs nothing but the product
   handle: assets/twrd-quick-add.js listens for clicks on [data-twrd-quickadd]
   through a delegated listener on document, and fetches /products/<handle>.js
   itself. data-twrd-fabric and data-twrd-variant are optional hints there, and
   the dialog derives both when they are missing.

   Idempotent: a card that already has a button is skipped, so the vendor
   reordering cards (it moves the same nodes around) never duplicates one. */
(function () {
  'use strict';

  if (window.__twrdCartRecQuickInit) return;
  window.__twrdCartRecQuickInit = true;

  var CONTAINER = '[cart-recommendations-content]';

  /* The drawer renders the mini card (settings.cart_show_recommendations with
     mini cards on), the cart page can render the full one. Both are hydrated
     the same way and both wrap their image in .o-ratio__content, so both are
     matched here. The mini card carries only a "Quick view" link of its own,
     which is what left the drawer with no add at all. */
  var CARD = 'product-card, product-card-mini';

  /* Same bag as snippets/product-card.liquid, so the drawer and the grid do not
     drift into two different icons. */
  var BAG_SVG =
    '<svg class="twrd-qa-btn__icon" viewBox="0 0 48 48" fill="none" stroke="currentColor"' +
    ' stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M27.5 43.5H5V14.4048H43V28.2611M34 14.1984V13.8105C34 8.66844 29.702 4.5 24.4' +
    ' 4.5H23.6C18.2981 4.5 14 8.66844 14 13.8105V14.1984M35 28.746V35.5349M35 35.5349V42.3238' +
    'M35 35.5349H28M35 35.5349L42 35.5349"/></svg>';

  /* The handle, taken from the link the vendor element writes onto the card.
     Reading the DOM rather than the element's `product` property keeps this
     working whichever way a future theme version hydrates the card. */
  function handleOf(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var href = link.getAttribute('href') || '';
    var match = href.match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (err) {
      return match[1];
    }
  }

  function titleOf(card) {
    var el =
      card.querySelector('.product-card__title') ||
      card.querySelector('a[product-card-link] .visually-hidden');
    var text = el ? (el.textContent || '').trim() : '';
    return text || 'this product';
  }

  /* Same heart as snippets/product-card.liquid. The click handling, the paint
     on wishlist changes and the styling all live in snippets/twrd-card-wishlist
     (rendered on every page from layout/theme.liquid) and are delegated on
     [data-twrd-card-wish], so the button only has to carry the same attributes. */
  var HEART_SVG =
    '<svg class="twrd-card-wish__icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

  /* The visually hidden link text keeps the full product title; the visible
     title may have been shortened to the motif by the drawer script. */
  function fullTitleOf(card) {
    var hidden = card.querySelector('a.product-card__link .visually-hidden, a[product-card-link] .visually-hidden');
    var text = hidden ? (hidden.textContent || '').trim() : '';
    return text || titleOf(card);
  }

  function isSaved(handle) {
    try {
      if (window.TwrdWishlist && typeof window.TwrdWishlist.has === 'function') return window.TwrdWishlist.has(handle);
      return JSON.parse(localStorage.getItem('twrd_wishlist') || '[]').some(function (p) { return p.handle === handle; });
    } catch (_) { return false; }
  }

  function addHeart(card, mount, handle) {
    var link = card.querySelector('a[href*="/products/"]');
    var img = card.querySelector('img');
    /* .money first: the price wrapper also holds a visually hidden "Regular price" label. */
    var price = card.querySelector('.product-card__price .money') || card.querySelector('.product-card__price');
    var url = '';
    try { url = link ? new URL(link.getAttribute('href'), window.location.origin).href.split('?')[0] : ''; } catch (_) {}

    var heart = only(card, mount, '.twrd-card-wish');
    if (!heart) {
      heart = document.createElement('button');
      heart.innerHTML = HEART_SVG;
      mount.appendChild(heart);
    }
    heart.type = 'button';
    heart.className = 'twrd-card-wish twrd-card-wish--cartrec';
    heart.setAttribute('data-twrd-card-wish', '');
    heart.setAttribute('data-product-handle', handle);
    heart.setAttribute('data-product-id', card.getAttribute('product-id') || '');
    heart.setAttribute('data-product-title', fullTitleOf(card));
    /* The drawer thumbnail is a 180px crop. The wishlist page shows the image
       large, so ask the CDN for a 600px version of the same file. */
    var src = img ? (img.currentSrc || img.getAttribute('src') || '') : '';
    src = src.replace(/([?&])width=\d+/, '$1width=600').replace(/&height=\d+/, '').replace(/&crop=[a-z]+/, '');
    heart.setAttribute('data-product-image', src);
    heart.setAttribute('data-product-url', url);
    heart.setAttribute('data-product-price', price ? (price.textContent || '').replace(/\s+/g, ' ').trim() : '');
    var saved = isSaved(handle);
    heart.setAttribute('aria-pressed', saved ? 'true' : 'false');
    heart.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Save to wishlist');
  }

  /* Idempotency, the hard version.

     The vendor element does not only build cards, it MOVES and REUSES them:
     the same node can be prepended to the row and then handed a different
     product. Two failure modes follow from that, and both were visible in the
     drawer:

     1. a moved node arrives before it carries a product link, so the first
        decorate() found no handle and returned. Nothing re-ran for that card,
        so it kept its place in the row with no quick add and no heart.
     2. a reused node keeps buttons that point at the PREVIOUS product, or the
        vendor replaces .o-ratio__content and the buttons leave with it.

     only() answers both: it returns the single live button inside the current
     mount and deletes every stray copy elsewhere in the card. decorate() then
     rewrites the attributes each pass, so a reused card is re-pointed at its
     new product instead of being skipped as "already done". */
  function only(card, mount, selector) {
    var all = card.querySelectorAll(selector);
    var keep = null;
    for (var i = 0; i < all.length; i += 1) {
      if (!keep && all[i].parentNode === mount) keep = all[i];
      else all[i].parentNode.removeChild(all[i]);
    }
    return keep;
  }

  /* True once the card carries the two live buttons for the product it shows
     right now. A card that fails this is retried, not abandoned. */
  function isDecorated(card) {
    var mount = card.querySelector('.o-ratio__content');
    if (!mount) return false;
    var button = mount.querySelector('.twrd-qa-btn');
    var heart = mount.querySelector('.twrd-card-wish');
    if (!button || !heart) return false;
    return button.getAttribute('data-twrd-quickadd') === handleOf(card);
  }

  function decorate(card) {
    if (!card) return false;

    /* .o-ratio__content is the positioned box the Liquid card puts the button
       in. Without it the absolutely positioned button would escape the card. */
    var mount = card.querySelector('.o-ratio__content');
    if (!mount) return false;

    var handle = handleOf(card);
    if (!handle) return false;

    var button = only(card, mount, '.twrd-qa-btn');
    if (!button) {
      button = document.createElement('button');
      button.innerHTML = BAG_SVG;
      mount.appendChild(button);
    }
    button.type = 'button';
    button.className = 'twrd-qa-btn twrd-qa-btn--cartrec';
    button.setAttribute('data-twrd-quickadd', handle);
    button.setAttribute('aria-label', 'Quick add ' + titleOf(card));
    button.setAttribute('aria-haspopup', 'dialog');

    addHeart(card, mount, handle);
    return true;
  }

  /* ---- a product already in the bag is not a recommendation ----

     The vendor element filters the cart's own products out of the list
     (assets/cart-recommendations.js), but it skips the variants it is adding
     right now, so a product added straight from this row survives that filter
     and keeps its card. That card is the one with no quick add and no heart:
     the vendor moves an existing node to the front instead of building a new
     one, and the moved node can arrive before it carries a product link.

     Simplest correct answer: never recommend what is already in the bag. The
     cart's product ids and handles come from /cart.js, cached here and
     refreshed on every cart event, so the rule survives each drawer
     re-render. */
  var cartIds = {};
  var cartHandles = {};
  var CART_URL = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js';

  function readCart(cart) {
    if (!cart || !cart.items) return;
    var ids = {};
    var handles = {};
    for (var i = 0; i < cart.items.length; i += 1) {
      var item = cart.items[i];
      if (item.product_id != null) ids[String(item.product_id)] = true;
      if (item.handle) handles[String(item.handle)] = true;
    }
    cartIds = ids;
    cartHandles = handles;
  }

  var fetching = false;
  function refreshCart(done) {
    if (fetching) return;
    fetching = true;
    window
      .fetch(CART_URL, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        fetching = false;
        if (!cart) return;
        readCart(cart);
        if (done) done();
      })
      .catch(function () { fetching = false; });
  }

  function inCart(card) {
    var id = card.getAttribute('product-id');
    if (id && cartIds[String(id)]) return true;
    var handle = handleOf(card);
    return !!(handle && cartHandles[handle]);
  }

  /* An empty row would leave a bare cream band in the drawer, so the section
     hides itself once nothing is left to show. */
  function pruneEmptyRows() {
    var rows = document.querySelectorAll('cart-recommendations');
    for (var i = 0; i < rows.length; i += 1) {
      var content = rows[i].querySelector(CONTAINER);
      if (!content) continue;
      if (content.children.length === 0) rows[i].setAttribute('hidden', '');
    }
  }

  /* Only cards inside a recommendation container. Every other card on the page
     is a Liquid card that already decided, in Liquid, whether it gets a bag
     button; adding one here would override that decision. */
  function inScope(card) {
    return !!(card.closest && card.closest(CONTAINER));
  }

  /* Returns true when at least one card in a recommendation row is still
     undecorated, which is the signal to keep retrying. */
  function scan(root) {
    if (!root || root.querySelectorAll === undefined) return false;
    var cards = root.querySelectorAll(CARD);
    var pruned = false;
    var todo = false;
    for (var i = 0; i < cards.length; i += 1) {
      var card = cards[i];
      if (!inScope(card)) continue;
      /* inCart() reads the product id first and the handle second. A card that
         carries neither yet answers false, which is right: it is not "not in
         the cart", it is not readable yet, so it stays and the next pass
         decides. */
      if (inCart(card)) {
        card.remove();
        pruned = true;
        continue;
      }
      if (!decorate(card) || !isDecorated(card)) todo = true;
    }
    if (pruned) pruneEmptyRows();
    return todo;
  }

  /* One debounced observer for the whole document.

     Debounced rather than reacting to each inserted node, because the vendor
     inserts an EMPTY card first and fills it in afterwards
     (container.prepend(card); card.product = item). At insertion there is no
     product link yet, so handleOf() would find no handle and the card would be
     skipped forever. Re-scanning shortly after any change catches the card once
     it is populated, and decorate() is idempotent so repeat scans cost nothing.

     Watching the whole document, not the container: the drawer is cloned in and
     out of the page, so a reference taken at load time stops being the live
     container the first time it rebuilds itself. */
  /* A card that is not ready on the first pass is retried on this ladder: one
     animation frame, then a widening series of timeouts out to ~3.2s. The
     ladder is re-armed by every trigger (mutation, cart event, drawer open) and
     stops as soon as every card in the row is decorated, so it never becomes a
     permanent timer. Bounded on purpose: a card the vendor never populates must
     not keep the page busy. */
  var RETRY_STEPS = [60, 140, 300, 600, 1000, 1600, 2400, 3200];

  /* Naar stigen er koert til ende og et kort STADIG mangler sine knapper,
     giver vi ikke op. Det var det forrige fejlbillede: det foerste kort i
     raekken stod uden quick add og uden hjerte, ogsaa efter fem sekunder.
     To ting er tilfoejet, begge begivenhedsdrevne:

     1. et billede der lander efter stigen. Safari kan bruge laengere end
        3,2 sekunder paa den foerste thumbnail paa en langsom forbindelse,
        og vendor-elementet saetter src FOER det saetter linket faerdigt.
        Et engangs load/error paa hvert ufaerdigt korts <img> scanner igen
        praecis naar billedet er der.
     2. en vagt med voksende interval der koerer saa laenge et kort i
        raekken mangler. Den stopper i samme oejeblik alt er dekoreret,
        holder pause mens fanen er skjult, og armes forfra af enhver
        mutation, kurv-begivenhed eller aabning af skuffen.

     decorate() er idempotent og only() fjerner stray-knapper, saa en
     ekstra scanning kan aldrig give to knapper paa samme kort. */
  var WATCH_MIN = 500;
  var WATCH_MAX = 4000;
  var WATCH_WINDOW = 30000;

  function start() {
    var scanRoot = document.getElementById('modal-cart') || document.querySelector('.cart--page') || document;
    var pending = 0;
    var timers = [];
    var watchTimer = null;
    var watchDelay = WATCH_MIN;
    var watchUntil = 0;
    var hooked = typeof WeakSet === 'function' ? new WeakSet() : null;
    var hookedList = [];

    function clearRetries() {
      for (var i = 0; i < timers.length; i += 1) clearTimeout(timers[i]);
      timers = [];
    }

    function stopWatchdog() {
      if (watchTimer) clearTimeout(watchTimer);
      watchTimer = null;
      watchDelay = WATCH_MIN;
    }

    function pumpWatchdog() {
      if (watchTimer) return;
      if (Date.now() > watchUntil) return;
      watchTimer = setTimeout(function () {
        watchTimer = null;
        /* En skjult fane maaler intet rigtigt og maa ikke bruge tid.
           Vi venter og proever igen med samme interval. */
        if (document.hidden) { pumpWatchdog(); return; }
        if (scan(scanRoot)) {
          hookPending();
          watchDelay = Math.min(WATCH_MAX, Math.round(watchDelay * 1.6));
          pumpWatchdog();
        } else {
          stopWatchdog();
        }
      }, watchDelay);
    }

    /* Et engangs-hook pr. billede. Kortet kan ikke laeses foer vendor-
       elementet har skrevet baade src og href, og billedets load er det
       sidste af de to i praksis. */
    function hookPending() {
      var cards = scanRoot.querySelectorAll(CARD);
      for (var i = 0; i < cards.length; i += 1) {
        var card = cards[i];
        if (!inScope(card) || isDecorated(card)) continue;
        var img = card.querySelector('img');
        if (!img) continue;
        if (hooked ? hooked.has(img) : hookedList.indexOf(img) !== -1) continue;
        if (hooked) hooked.add(img); else hookedList.push(img);
        img.addEventListener('load', schedule, { once: true });
        img.addEventListener('error', schedule, { once: true });
      }
    }

    function tick() {
      if (scan(scanRoot)) {
        hookPending();
        pumpWatchdog();
      } else {
        clearRetries();
        stopWatchdog();
      }
    }

    function armRetries() {
      clearRetries();
      watchUntil = Date.now() + WATCH_WINDOW;
      for (var i = 0; i < RETRY_STEPS.length; i += 1) {
        timers.push(setTimeout(tick, RETRY_STEPS[i]));
      }
      if (window.requestAnimationFrame) window.requestAnimationFrame(tick);
    }

    function rescan() {
      pending = 0;
      if (scan(scanRoot)) {
        hookPending();
        armRetries();
        pumpWatchdog();
      } else {
        clearRetries();
        stopWatchdog();
      }
    }

    function schedule() {
      if (pending) return;
      pending = setTimeout(rescan, 120);
    }

    /* #cart-data er serverens kurv fra sidste sideindlaesning. Den er gratis
       og god nok til foerste tegning. Men scriptet indlaeses foerst naar
       skuffen aabner, saa et kob der skete inden da staar ikke i den. Vi
       sammenligner derfor med de linjer skuffen faktisk viser: passer tallet
       ikke, henter vi kurven een gang. */
    var seeded = false;
    /* Bedste kilde: den kurv temaet sidst sendte med on:cart:loaded.
       layout/theme.liquid gemmer den. Den er altid nyere end #cart-data. */
    if (window.__twrdCart && window.__twrdCart.items) {
      readCart(window.__twrdCart);
      seeded = true;
    }
    var cartData = seeded ? null : document.getElementById('cart-data');
    if (cartData) {
      try {
        var initialCart = JSON.parse(cartData.textContent || '{}');
        if (initialCart && initialCart.items) {
          var drawnLines = document.querySelectorAll('#modal-cart cart-item[key]').length;
          if (drawnLines === initialCart.items.length) {
            readCart(initialCart);
            seeded = true;
          }
        }
      } catch (_) {}
    }
    if (!seeded) refreshCart(rescan);
    rescan();

    /* The store publishes the new cart with every add, change and remove, so
       the id list is current before the next scan runs. Both events are
       listened for: cart:loaded carries the new cart, cart:change fires on
       quantity edits, and each one is followed by a drawer re-render. */
    function onCart(e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && cart.items) readCart(cart);
      else refreshCart(rescan);
      schedule();
      armRetries();
    }
    document.addEventListener('on:cart:loaded', onCart);
    document.addEventListener('on:cart:change', onCart);

    /* The drawer is built, and its recommendations fetched, while the modal
       opens. Re-arming here covers the first paint of a freshly rendered row. */
    document.addEventListener('on:modal:opened', armRetries);
    document.addEventListener('on:modal:opening', armRetries);
    document.addEventListener('on:items-scroll:change', schedule);

    /* Safari saetter timere paa pause i en skjult fane og genbruger siden
       fra bfcache. Begge veje tilbage skal ende i en ny scanning, ellers
       staar et halvfaerdigt kort tilbage fra foer pausen. */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) { schedule(); armRetries(); }
    });
    window.addEventListener('pageshow', function () { schedule(); armRetries(); });

    /* Two observers, on purpose.

       The document one catches the drawer being cloned in and out of the page,
       so a row that did not exist at load time is still seen. The per-row one
       is narrower and watches the attributes the vendor writes LAST when it
       fills a moved card in (href on the link, src on the image); the document
       observer would have to filter those out of every mutation on the page. */
    var watched = typeof WeakSet === 'function' ? new WeakSet() : null;
    var seen = [];

    function isWatched(row) {
      if (watched) return watched.has(row);
      return seen.indexOf(row) !== -1;
    }

    function markWatched(row) {
      if (watched) watched.add(row);
      else seen.push(row);
    }

    function watchRows() {
      var rows = scanRoot.querySelectorAll(CONTAINER);
      for (var i = 0; i < rows.length; i += 1) {
        if (isWatched(rows[i])) continue;
        markWatched(rows[i]);
        new MutationObserver(schedule).observe(rows[i], {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['href', 'src', 'srcset', 'product-id', 'rendered']
        });
        armRetries();
      }
    }

    watchRows();

    new MutationObserver(function () {
      watchRows();
      schedule();
    }).observe(scanRoot, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
